// Paket 09: what every deletion dialog shows before anyone may confirm. Pure functions, so the
// wording and the confirm rule are testable without a browser (frontend/tests/deletion-preview.test.mjs).

export type DeletionBlocker = { code: string; message: string };
export type DeletionPreview = {
  scope: 'ORGANIZATION' | 'USER';
  subjectId: string;
  subjectLabel: string | null;
  removed: Record<string, number>;
  kept: Record<string, number>;
  retained: Record<string, number>;
  blockers: DeletionBlocker[];
  confirmation?: { token: string; expiresAt: string };
};
export type DeletionRow = { key: string; label: string; count: number };

const labels: Record<string, string> = {
  users: 'Konten',
  chatSessions: 'Rechercheverläufe',
  searchLogs: 'Recherche-Protokolle',
  comparisons: 'Vergleichsläufe',
  answerRatings: 'Antwortbewertungen',
  sessions: 'Anmeldesitzungen',
  logEvents: 'Anwendungsprotokoll-Einträge',
  invitations: 'Einladungen',
  mails: 'Nachrichten im Postausgang',
  costEntries: 'Kostenbuchungen',
  reservations: 'Kostenreservierungen',
  credits: 'Gutschriften',
  testInvoices: 'Musterbelege', // Plan 1a Welle 1 (W1-G13)
  payments: 'Zahlungsaufträge',
  quotes: 'Angebote',
  subscriptions: 'Abonnement',
  roles: 'Zusatzrollen',
  sharedResearch: 'Geteilte Momentaufnahmen',
  // Profil-Nachbau F-12 Punkt 7 (PF-713 zählt sie in operator/deletionJob.ts, PF-716): die Schlüsselkontakte der Firma.
  contacts: 'Schlüsselkontakte',
};
// Paket 16, Schritt 8 (M-11.12): was bleibt, sagt, in welcher Form es bleibt; Anmeldeereignisse behalten die IP-Adresse.
const keptLabels: Record<string, string> = {
  auditHistory: 'Einträge der Verwaltungshistorie (anonymisiert)',
  auditJournal: 'Einträge im Auditjournal (ohne Namen, E-Mail-Adresse und Begründung)',
  securityEvents: 'Anmelde- und Passwortereignisse (ohne Kontobezug, mit IP-Adresse bis Fristende)',
  costEntries: 'Kostenbuchungen (ohne Personenbezug)',
  payments: 'Zahlungsaufträge (ohne Personenbezug)',
  // Paket 10 B10 (KAN-31): eine Momentaufnahme gehört der Organisation; mit dem Konto geht nur der Name des Einstellers.
  sharedResearch: 'Geteilte Momentaufnahmen (Titel und Inhalt bleiben bei der Firma, ohne Namen der Person)',
  providerEvents: 'Ereignisse des Zahlungsdienstes (ohne Kundenbezug)',
  // Paket 12 K12-7 (RSM-32): die Zählung liefert Paket 16 (operator/deletionJob.ts, N12-3).
  legalAcceptances: 'Vertragsannahmen (Nachweis mit Version und Zeitpunkt, ohne Namen)',
  // Stripe-Start Ticket 07: billing_trial_claims bleiben bis zum Fristende im Aufbewahrungsregister (TRIAL_CLAIMS).
  trialClaims: 'Genutzte Testphasen (Schlüssel-Hash der Adresse, ohne Adresse)',
};
const retainedLabels: Record<string, string> = {
  invoices: 'Rechnungen (bleiben als Beleg im Belegarchiv)',
};

function label(key: string, table: Record<string, string>) {
  if (key.startsWith('solo.')) { const inner = key.slice(5); return `Einzelkonto: ${table[inner] || labels[inner] || inner}`; }
  return table[key] || key;
}
// Zero counts are left out; an unknown kind is shown by its key rather than hidden.
function rows(values: Record<string, number> | undefined, table: Record<string, string>): DeletionRow[] {
  return Object.entries(values || {}).filter(([, count]) => count > 0).map(([key, count]) => ({ key, label: label(key, table), count }));
}
export function deletionRows(preview: DeletionPreview) {
  return { removed: rows(preview.removed, labels), kept: rows(preview.kept, keptLabels), retained: rows(preview.retained, retainedLabels) };
}
// A deletion cannot be undone: confirm only with a loaded preview, its proof and no known obstacle.
export function mayConfirm(preview: DeletionPreview | undefined | null) {
  return !!preview && !!preview.confirmation?.token && preview.blockers.length === 0;
}
